<template>
  <section class="form-fw__content">
    <form
      v-if="status !== isFailed"
      class="form--custom"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <fieldset
        class="form__container"
        :disabled="status === isLoading"
      >
        <header class="form__container__header">
          <h2 class="form__container__title">{{ $t(`${namespace}.containerTitle`) }}</h2>
          <p class="form__container__desc">{{ $t(`${namespace}.containerDesc`) }}</p>
        </header>

        <div class="form__container__content">
          <label>{{ $t(`${namespace}.fieldCompanyGroupLabel`) }}</label>
          <v-text-field
            v-model="form.name"
            :placeholder="$t(`${namespace}.fieldCompanyGroupPlaceholder`)"
            :error-messages="errors.collect($t(`${namespace}.fieldCompanyGroupName`))"
            v-validate="{
              required: true,
              min: 3,
              max: 255
            }"
            :data-vv-name="$t(`${namespace}.fieldCompanyGroupName`)"
            required
            flat
            outline
            solo
            color="accent"
          />

          <label>{{ $t(`${namespace}.fieldCompanyGroupListLabel`) }}</label>
          <div class="form__group">
            <transition name="fade">
              <AutoComplete
                v-if="!isFetching"
                kind="company"
                :data="companyGroup"
                :placeholder="$t(`${namespace}.fieldCompanyGroupListPlaceholder`)"
                multiple
                @input="updateCompanyGroup"
              />
            </transition>
          </div>
        </div>
      </fieldset>
    </form>

    <div
      v-if="status === isFailed"
      class="text-xs-center mt-5"
    >
      <span>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          href="#"
          :title="$t('Common.tryAgain')"
          class="text--link accent--text"
          @click.prevent="$emit('tryAgain')"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </span>
    </div>
  </section>
</template>

<script>
import { mapActions } from 'vuex'
import { isObject, isArray, goBack } from '@/utils/functions'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import AutoComplete from 'components/AutoComplete'

export default {
  name: 'EmployeeForm',
  props: {
    kind: {
      type: String
    },
    data: {
      type: Object
    },
    submit: {
      type: Boolean
    },
    status: {
      type: Number
    }
  },
  components: {
    AutoComplete
  },
  computed: {
    isLoading: () => STATUS_IS_LOADING,
    isSuccess: () => STATUS_SUCCESS,
    isFailed: () => STATUS_FAILED
  },
  data () {
    return {
      namespace: 'Views.CompanyGroupPage.form',
      isFetching: false,
      companyGroup: [],
      isCompanyCompanyValid: true,
      form: {
        name: null,
        companyIds: []
      }
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error'
    }),

    isValidObject: (val) => isObject(val),

    isValidArray: (val) => isArray(val),

    prevPage () {
      goBack()
    },

    setCompanyGroupData () {
      if (this.kind === 'editar' && this.isValidObject(this.data)) {
        this.isFetching = true

        const { companyGroup = null, companies = null } = this.data
        const id = companyGroup && companyGroup.id ? companyGroup.id : null
        const name = companyGroup && companyGroup.name ? companyGroup.name : null
        const companyIds = companies.map(item => item.id)

        this.companyGroup = companies.map(item => ({
          id: item.id,
          name: item.name
        }))

        this.form = {
          id,
          name,
          companyIds
        }

        setTimeout(() => { this.isFetching = false }, 200)
      }
    },

    resetFields () {
      this.form = {
        name: null,
        companyIds: []
      }
    },

    updateCompanyGroup (val) {
      if (this.isValidArray(val)) {
        this.companyGroup = val.map(item => ({
          id: item.id,
          name: item.name
        }))

        this.form.companyIds = val.map(item => item.id)
        this.isCompanyCompanyValid = true
        return
      }

      this.companyGroup = []
      this.form.companyIds = []
      this.isCompanyCompanyValid = false
    },

    errorHandler (err) {
      if (err && err.response && err.response.data && this.isValidObject(err.response.data.errors)) {
        const errors = err.response.data.errors
        const key = Object.keys(errors)

        if (this.isValidArray(errors[key])) {
          this.errorAlert(`${key}: ${errors[key][0]}`)
          return
        }

        this.errorAlert(this.$t(`${this.namespace}.createErrorMessage`))
        return
      }

      this.errorAlert(this.$t(`${this.namespace}.createErrorMessage`))
    },

    redirectToGroupCompanyTab () {
      this.$router.push({
        name: 'Company',
        query: { tab: 'companyGroup' }
      })
    },

    dispatchHeaderErrorMessage () {
      const { errors } = this.$validator
      // const errorsLength = errors.items.length === fields.items.length
      // const companyIdsLength = this.isCompanyCompanyValid ? 0 : 1
      // const errorsLength = (errors.items.length + companyIdsLength) > 0
      const errorsLength = errors.items.length > 0
      this.$emit('headerErrorMessage', errorsLength)
    },

    async create () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        await this.$apiService.companyGroup.create({ ...this.form })
        this.successAlert(this.$t(`${this.namespace}.createSuccessMessage`))
        this.$emit('setCurrentStatus', this.isSuccess)
        scrollTo(0, 0)
        this.redirectToGroupCompanyTab()
        this.resetFields()
      } catch (err) {
        this.errorHandler(err)
        this.$emit('setCurrentStatus', this.isSuccess)
      }
    },

    async edit () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        const { id, name, companyIds } = this.form
        await this.$apiService.companyGroup.update(id, { name, companyIds })
        this.successAlert(this.$t(`${this.namespace}.editSuccessMessage`))
        scrollTo(0, 0)
        this.redirectToGroupCompanyTab()
        // this.$emit('tryAgain')
      } catch (err) {
        this.errorHandler(err)
        this.$emit('setCurrentStatus', this.isSuccess)
      }
    },

    async handleSubmit () {
      try {
        const isValid = await this.$validator.validateAll()
        this.dispatchHeaderErrorMessage()

        if (isValid) {
          this.kind === 'editar'
            ? this.edit()
            : this.create()
        } else {
          const target = this.$validator.errors.items[0]
          if (target) {
            const elem = this.$el.querySelector(`[data-vv-name="${target.field}"]`)
            elem.scrollIntoView(false)
            elem.focus()
          }
        }
      } catch (err) {
        console.log('ERROR', err)
      }
    }
  },
  watch: {
    kind () {
      this.setCompanyGroupData()
    },

    data: {
      deep: true,
      handler () {
        this.setCompanyGroupData()
      }
    },

    submit () {
      this.handleSubmit()
    }
  },
  created () {
    this.setCompanyGroupData()
  }
}
</script>

<style lang="scss" scoped>
.form__group {
  min-height: 74px;
}
</style>
